import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  /** Comma-separated or array — targets comparison / long-tail queries */
  keywords?: string | string[];
  /** Defaults to index,follow for marketing pages */
  robots?: string;
  /** For blog articles */
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    tags?: string[];
  };
}

const SITE_NAME = 'Retrosive';
const BASE_URL = 'https://retrosive.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;
const DEFAULT_DESCRIPTION =
  'Run better agile retrospectives with Retrosive. Real-time collaboration, 12+ templates, anonymous feedback, and action item tracking. Free to start.';

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export const useSEO = ({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  keywords,
  robots = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  article,
}: SEOProps) => {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonicalPath = canonical?.startsWith('http')
      ? new URL(canonical).pathname
      : canonical;
    const canonicalUrl = canonicalPath ? `${BASE_URL}${canonicalPath}` : BASE_URL;
    const keywordStr = Array.isArray(keywords) ? keywords.join(', ') : keywords;

    // Document title
    document.title = fullTitle;

    // Basic meta
    setMeta('description', description);
    setMeta('robots', robots);
    if (keywordStr) setMeta('keywords', keywordStr);

    // Canonical
    setLink('canonical', canonicalUrl);

    // Open Graph
    setMeta('og:type', ogType, 'property');
    setMeta('og:url', canonicalUrl, 'property');
    setMeta('og:title', fullTitle, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:image', ogImage, 'property');
    setMeta('og:site_name', SITE_NAME, 'property');

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);

    // Article-specific tags
    if (ogType === 'article' && article) {
      if (article.publishedTime) {
        setMeta('article:published_time', article.publishedTime, 'property');
      }
      if (article.modifiedTime) {
        setMeta('article:modified_time', article.modifiedTime, 'property');
      }
      if (article.author) {
        setMeta('article:author', article.author, 'property');
      }
      if (article.tags) {
        article.tags.forEach((tag) => {
          // Multiple article:tag tags — append each time (simplified: set the first only)
          setMeta('article:tag', tag, 'property');
        });
      }
    }
  }, [title, description, canonical, ogImage, ogType, keywords, robots, article]);
};
